import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { add } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { getSelectedAdAccountTimezone } from '../../../../../../modules/selectors/adAccounts'
import { createGamLineItemForecastService } from '../../../../../../modules/services/googleAdManager'
import { gamRightUploadAdDataSelector } from '../../../../../../modules/selectors/rights'
import { selectedPartnershipSelector } from '../../../../../../modules/selectors/partnerships'
import { selfAccountSelector } from '../../../../../../modules/selectors/selfAccounts'

import { formatDateWithTimeToBE } from '../../../../../../helpers/date'

import { GOOGLE_AD_MANAGER_AD_ACCOUNT } from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/fields'
import { GAM_NETWORK_CODE } from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/fields'
import { MEDIA_HEIGHT, MEDIA_WIDTH } from '../../../../../ReusableFormFields/AdFileUpload/fields'
import { GOOGLE_AD_MANAGER_PLATFORM } from '../../../../../../constants/selectLists/platformList'

// Remove properties whose value is null from objects (recursively).
const purifyObject = value => {
  if (Array.isArray(value)) {
    return value.map(purifyObject).filter(v => v !== null) // remove null array items
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [k, v]) => {
      if (v === null) return acc // skip keys with null values
      const purified = purifyObject(v)
      // keep undefined and other falsy values as-is, only skip explicit nulls
      acc[k] = purified
      return acc
    }, {})
  }
  return value
}

export const useFetchLineItemAdsForecast = ({ uploadedCreatives }) => {
  const { lineItem } = useSelector(gamRightUploadAdDataSelector)
  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const selfAccount = useSelector(selfAccountSelector)

  const { [GOOGLE_AD_MANAGER_AD_ACCOUNT]: gamAccount } = selectedPartnership
  const gamAccountId = gamAccount?.external_id
  const gamNetworkCode = selfAccount?.[GAM_NETWORK_CODE]

  // remove null values from lineItem object, in other case we get validation errors for these fields
  const purifiedLineItem = useMemo(() => {
    // fetch forecast only with uploaded creatives
    const lineItemCreatives = lineItem?.creative_placeholders || []

    // leave only creatives that are uploaded it could be checked by sizes
    const purifiedCreatives = lineItemCreatives.filter(creative => {
      const lineItemCreativeHeight = creative?.size?.height
      const lineItemCreativeWidth = creative?.size?.width

      // check if the creative is uploaded
      const creativeIsUploaded = uploadedCreatives.find(uploadedCreative => {
        const uploadedCreativeHeight = uploadedCreative?.[MEDIA_HEIGHT]
        const uploadedCreativeWidth = uploadedCreative?.[MEDIA_WIDTH]
        return uploadedCreativeHeight === lineItemCreativeHeight && uploadedCreativeWidth === lineItemCreativeWidth
      })

      return creativeIsUploaded
    })

    return purifyObject({
      ...lineItem,
      creative_placeholders: purifiedCreatives
    })
  }, [lineItem, uploadedCreatives])

  const forecastRequestData = useMemo(() => {
    const gamTimezone = getSelectedAdAccountTimezone(GOOGLE_AD_MANAGER_PLATFORM)
    const nowInGamTimezone = toZonedTime(new Date(), gamTimezone)
    // time should be calculated to ad account timezone
    // GAM allows fetch only for future date_start
    const futureDate = add(nowInGamTimezone, { minutes: 1 })

    return {
      network_code: gamNetworkCode,
      line_item: {
        line_item: {
          ...purifiedLineItem,
          // date should always be in future, so we add 1 minute to now in GAM timezone
          date_start: formatDateWithTimeToBE(futureDate)
        },
        advertiser_id: gamAccountId
      }
    }
  }, [purifiedLineItem, gamAccountId, gamNetworkCode])

  const uploadedSizesKey = useMemo(() => {
    if (!uploadedCreatives?.length) return ''
    return uploadedCreatives
      .map(c => {
        const w = c?.[MEDIA_WIDTH]
        const h = c?.[MEDIA_HEIGHT]
        if (!w || !h) return ''

        return `${w}x${h}`
      })
      .join('|')
  }, [uploadedCreatives])

  const { data, error, isLoading } = useQuery({
    queryKey: ['lineItemAdsForecast', uploadedSizesKey],
    queryFn: () => createGamLineItemForecastService(forecastRequestData),
    enabled: uploadedCreatives?.length > 0,
    retry: false
  })

  return {
    data,
    error,
    isLoading
  }
}
